import cuid from "cuid";
import {
  defaults,
  defaultsDeep,
  filter,
  get,
  isEmpty,
  keyBy,
  map,
  set,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useEffect, useState } from "react";
import { useLazyQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { KEYS, CONTACT_POINT_SYSTEM, RELATIONSHIP } from "utils/constants";
import Template from "../Template";
import View from "./View";

const RelatedPersons = ({
  currentStatus,
  tracer,
  project,
  onBack,
  onNext,
  updateUrl,
}) => {
  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);
  const [getSubscription, { data }] = useLazyQuery(
    graphql.queries.SUBSCRIPTION,
    { fetchPolicy: "no-cache" },
  );

  useEffect(() => {
    if (subscriptionId)
      getSubscription({
        variables: { where: { id: subscriptionId } },
        onCompleted: ({ subscription }) => {
          const subscriptionData = omitDeep(subscription, "__typename");
          const children = filter(
            get(subscriptionData, "project.contact.relatedPersons", []),
            {
              relationship: RELATIONSHIP.CHILD,
            },
          );
          if (!isEmpty(children))
            children.forEach((child) => {
              set(
                child,
                "identity.fields.teletransmission",
                get(child, "identity.fields.teletransmission", true),
              );
              set(
                child,
                "identity.telecoms",
                defaults(keyBy(get(child, "identity.telecoms"), "system"), {
                  [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: { value: "France" },
                }),
              );
            });

          const spouse = get(
            subscriptionData,
            "project.contact.relatedPersons",
            [],
          ).find(({ relationship }) => relationship === RELATIONSHIP.SPOUSE);
          if (!isEmpty(spouse)) {
            const spouseTelecoms = defaults(
              keyBy(get(spouse, "identity.telecoms"), "system"),
              {
                [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: { value: "France" },
              },
            );
            set(spouse, "identity.telecoms", spouseTelecoms);
          }
          const initialFormValues = {
            children,
            spouse: isEmpty(spouse)
              ? undefined
              : defaultsDeep(spouse, {
                  identity: { fields: { teletransmission: true } },
                }),
          };
          setInitialValues(initialFormValues);
          setLoading(false);
        },
      });
  }, []);

  const onSubmit = ({ spouse, children }) => {
    let updatedSpouse = spouse || {};
    let updatedChildren = children || [];

    if (!isEmpty(get(spouse, "identity"))) {
      updatedSpouse = {
        ...spouse,
        identity: {
          ...spouse.identity,
          telecoms: map(get(spouse, "identity.telecoms"), (item, key) =>
            defaults(item, { id: cuid(), system: key }),
          ),
        },
      };
    }

    if (!isEmpty(children)) {
      updatedChildren = children.map((child) => ({
        ...child,
        identity: {
          ...child.identity,
          telecoms: map(get(child, "identity.telecoms"), (item, key) =>
            defaults(item, { id: cuid(), system: key }),
          ),
        },
      }));
    }
    const relatedPersons = [
      ...(!isEmpty(updatedSpouse)
        ? [
            defaultsDeep(updatedSpouse, {
              id: cuid(),
              relationship: RELATIONSHIP.SPOUSE,
              identity: { id: cuid(), user: { id: cuid() } },
            }),
          ]
        : []),
      ...updatedChildren.map((child) =>
        defaultsDeep(child, {
          id: cuid(),
          relationship: RELATIONSHIP.CHILD,
          identity: { id: cuid(), user: { id: cuid() } },
        }),
      ),
    ];
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {
        contact: {
          id: get(project, "contact.id"),
          relatedPersons,
        },
      },
    });
  };
  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading}
      project={project}
      HeaderComponent={{
        subscription: get(data, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      View={
        <View
          initialValues={initialValues}
          locked={get(data, "subscription.locked", false)}
          onSubmit={onSubmit}
          onBack={onBack}
        />
      }
      contact={get(project, "contact")}
    />
  );
};
export default RelatedPersons;
